import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Box, Button, Typography } from '@mui/material';
import { getIsEmptyObj } from 'app/pages/loanApplication/LoanApplication.utils';
import { applicationPricingStatusApi } from 'app/services/api';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import FormGenerator from 'app/shared/componentsRestyled/common/formGenerator/FormGenerator';
import ListTableWrapper from 'app/shared/componentsRestyled/common/ListTableWrapper';
import ButtonGroupWrapper from 'app/shared/componentsRestyled/coreDesignSystem/Buttons/ButtonGroupWrapper';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';

import { COMPOSED_CLAIMS } from 'constants/composedClaims';
import { ConfirmationModalType } from 'constants/confirmationDialog';
import { FeatureFlag } from 'constants/featureFlags';
import { ExtensionType } from 'constants/loanApplication';
import { HORIZONTAL_ALIGNMENT } from 'constants/ui';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useForm from 'hooks/useForm';
import useLoanId from 'hooks/useLoanId';
import { getDefaultErrorMessage } from 'utils/axios';
import { getConditionalObject } from 'utils/data';
import { getConfigInfo } from 'utils/loanManagement';

import { selectIsClaimAllowed, selectMasterCompanyId } from 'store/auth/selectors';
import { openConfirmationDialogAction, showMessageAction } from 'store/global/actions';
import { getLockExtensionSettingsAction } from 'store/loanApplications/actions';
import {
  selectIsLoanOptimalBlueOrSifterEngineType,
  selectLockExtensionSettings,
  selectMaxExtensionPeriodDays,
} from 'store/loanApplications/selectors';
import { selectLLPATypesOptionsWithoutDeleted } from 'store/programs/selectors';

import { PropertyAndLoanTestIds } from '../../../../PropertyAndLoan.testIds';

import ExtensionItem from './ExtensionItem';
import { getExtensionConfig, lockExtensionFields } from './LockInfo.config';
import {
  CustomAdjustmentType,
  DEFAULT_ERROR_MESSAGE_LOCK_EXTENSION_COST_CALCULATION,
} from './LockInfo.constants';
import {
  controlsBlock,
  dialogCustomStyles,
  extensionItemsContainerStyles,
  formPartCustomStyles,
} from './LockInfo.styles';

const AddExtensionForm = ({
  isLoading,
  setIsLocalLoading,
  cancelForm,
  saveForm,
  currentExtensionsTermsSum,
}) => {
  const dispatch = useDispatch();

  const loanId = useLoanId();

  const { applicationId } = useParams();

  const isExternalLoanLockEnabled = useFeatureToggle(FeatureFlag.EnableExternalLoanLock);

  const llpaTypeOptions = useSelector(selectLLPATypesOptionsWithoutDeleted);
  const lockExtensionSettings = useSelector(selectLockExtensionSettings);
  const hasLockExtensionOverrideClaim = useSelector(
    selectIsClaimAllowed(COMPOSED_CLAIMS.LOCK_EXTENSIONS_OVERRIDE),
  );
  const isOBEngineType = useSelector(selectIsLoanOptimalBlueOrSifterEngineType);
  const maxExtensionPeriodDays = useSelector(selectMaxExtensionPeriodDays);
  const companyId = useSelector(selectMasterCompanyId);

  const [selectedExtension, setSelectedExtension] = useState(null);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [isLockExtensionsLoaded, setIsLockExtensionsLoaded] = useState(false);

  const observableFieldsNames = Object.keys(lockExtensionFields);
  const isLockExtensionOverrideAllowed = hasLockExtensionOverrideClaim;

  const filteredLockExtensionSettings = useMemo(() => {
    return lockExtensionSettings?.filter(
      ({ term }) => term <= maxExtensionPeriodDays - currentExtensionsTermsSum,
    );
  }, [lockExtensionSettings, maxExtensionPeriodDays, currentExtensionsTermsSum]);

  const isNoAvailableSettings =
    !filteredLockExtensionSettings.length &&
    isLockExtensionsLoaded &&
    !isLockExtensionOverrideAllowed;

  useEffectSafeStrictMode(() => {
    setIsDataLoading(true);

    dispatch(getLockExtensionSettingsAction({ loanId }))
      .then(() => setIsLockExtensionsLoaded(true))
      .catch(() => {
        if (isLockExtensionOverrideAllowed) {
          return;
        }

        cancelForm();
      })
      .finally(() => setIsDataLoading(false));
  }, [cancelForm, dispatch, isLockExtensionOverrideAllowed, loanId, setIsDataLoading]);

  useEffect(() => {
    if (!isNoAvailableSettings) {
      return;
    }

    dispatch(
      openConfirmationDialogAction({
        type: ConfirmationModalType.ERROR,
        message: 'No available settings',
        description:
          'There are no available lock extensions that fit the allowed maximum extension period',
      }),
    );
    cancelForm();
  }, [cancelForm, dispatch, isNoAvailableSettings]);

  const { initialFormValues, schema, fieldsConfig, config } = useMemo(() => {
    const config = getExtensionConfig({ llpaTypeOptions, isOBFlow: isOBEngineType });

    const { initialFormValues, schema, fieldsConfig } = getConfigInfo({
      applicationData: {},
      config,
    });

    return { initialFormValues, schema, fieldsConfig, config };
  }, [llpaTypeOptions, isOBEngineType]);

  const {
    form,
    handleChange,
    validationErrors,
    setSchema,
    setInForm,
    setMultipleInForm,
    isFormChanged,
    validateField,
    validateForm,
    staticValidateField,
    formPendingChanges,
    setValidationErrors,
  } = useForm(initialFormValues, fieldsConfig, observableFieldsNames);

  const isFormEmpty = getIsEmptyObj(form, { isFalsyCheck: isOBEngineType });

  const isDisabledAddButton = (!isFormChanged || isFormEmpty) && !selectedExtension;

  useEffect(() => {
    if (!isFormChanged) {
      return;
    }

    setSelectedExtension(null);
  }, [isFormChanged]);

  useEffect(() => {
    setSchema(schema);
  }, [setSchema, schema]);

  const openModal = useCallback(
    data => {
      dispatch(
        openConfirmationDialogAction({
          customDialogProps: {
            sx: dialogCustomStyles,
          },
          message:
            'Would you like to retain the current rate and add the extension adjustments to discount points or reprice the loan?',
          onAccept: () => {
            saveForm({
              form: {
                ...data,
                lockExtensionType: ExtensionType.REPRICE_LOAN,
              },
              type: CustomAdjustmentType.EXTENSION,
            });
          },
          onCancel: () => {
            saveForm({
              form: {
                ...data,
                lockExtensionType: ExtensionType.RETAIN_RATE,
              },
              type: CustomAdjustmentType.EXTENSION,
            });
          },
          onAcceptButtonName: 'Reprice Loan',
          onCancelButtonName: 'Retain Rate',
        }),
      );
    },
    [dispatch, saveForm],
  );

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      if (selectedExtension) {
        openModal({
          lockExtensionId: selectedExtension.lockExtensionId,
          term: selectedExtension.term,
          amount: selectedExtension.price,
          llpaTypeId: selectedExtension.llpaTypeId,
        });

        return;
      }

      if (!(await validateForm())) {
        return;
      }

      const formData = {
        term: Number(form.term),
        ...getConditionalObject(!isOBEngineType, () => ({
          amount: Number(form.amount),
          llpaTypeId: form.llpaType?.value,
        })),
      };

      if (isOBEngineType && isExternalLoanLockEnabled) {
        setIsLocalLoading(true);

        const { data } = await applicationPricingStatusApi
          .calculateLockExtensionCost({
            context: {
              companyId,
              applicationId,
            },
            lockExtensionDays: Number(form.term),
          })
          .catch(error => {
            dispatch(
              showMessageAction({
                message:
                  getDefaultErrorMessage(error) ||
                  DEFAULT_ERROR_MESSAGE_LOCK_EXTENSION_COST_CALCULATION,
                variant: 'error',
              }),
            );

            return {};
          })
          .finally(() => setIsLocalLoading(false));

        if (!data?.cost) {
          return;
        }

        saveForm({
          form: {
            ...formData,
            amount: data.cost,
          },
          type: CustomAdjustmentType.EXTENSION,
        });

        return;
      }

      openModal(formData);
    },
    [
      selectedExtension,
      validateForm,
      form,
      isOBEngineType,
      isExternalLoanLockEnabled,
      openModal,
      setIsLocalLoading,
      companyId,
      applicationId,
      saveForm,
      dispatch,
    ],
  );

  const onCancelClick = useCallback(() => {
    if (!isFormChanged) {
      cancelForm();

      return;
    }

    dispatch(
      openConfirmationDialogAction({
        type: ConfirmationModalType.WARNING,
        message: 'Cancel Changes',
        description: 'Are you sure you want to cancel changes?',
        onAccept: () => {
          cancelForm();
        },
      }),
    );
  }, [dispatch, isFormChanged, cancelForm]);

  const onSelectExtensionItem = useCallback(
    extensionItem => {
      setSelectedExtension(extensionItem);

      setMultipleInForm({ term: '', amount: '', llpaType: '' }, false);
      setValidationErrors({});
    },
    [setMultipleInForm, setValidationErrors],
  );

  if (isNoAvailableSettings) {
    return null;
  }

  return (
    <ListTableWrapper
      customStyles={formPartCustomStyles}
      title="Add New Extension"
      titleVariant="bold16"
    >
      <Box>
        <Typography variant="medium14">Select Adjustments</Typography>
        <Box sx={extensionItemsContainerStyles}>
          {filteredLockExtensionSettings.map(extensionItem => (
            <ExtensionItem
              extensionItem={extensionItem}
              isActive={extensionItem.lockExtensionId === selectedExtension?.lockExtensionId}
              key={extensionItem.lockExtensionId}
              onSelect={onSelectExtensionItem}
            />
          ))}
        </Box>
      </Box>
      <form noValidate data-ischanged={isFormChanged} onSubmit={onSubmit}>
        <Box>
          <FormGenerator
            config={config}
            form={form}
            formName={FormNames.ADD_EXTENSION}
            handleChange={handleChange}
            isDisabled={!isLockExtensionOverrideAllowed}
            isLoading={isLoading || isDataLoading}
            numberOfColumns={3}
            pendingChanges={formPendingChanges}
            setInForm={setInForm}
            staticValidateField={staticValidateField}
            validateField={validateField}
            validationErrors={validationErrors}
          />
          <ButtonGroupWrapper alignment={HORIZONTAL_ALIGNMENT.RIGHT} sx={controlsBlock}>
            <Button
              data-testid={PropertyAndLoanTestIds.ADD_EXTENSION_FORM_CANCEL_BUTTON}
              size="small"
              variant="outlined"
              onClick={onCancelClick}
            >
              Cancel
            </Button>
            <Button
              data-testid={PropertyAndLoanTestIds.ADD_EXTENSION_FORM_SUBMIT_BUTTON}
              disabled={isDisabledAddButton}
              size="small"
              type="submit"
            >
              Add
            </Button>
          </ButtonGroupWrapper>
        </Box>
      </form>
    </ListTableWrapper>
  );
};

AddExtensionForm.propTypes = {
  cancelForm: PropTypes.func.isRequired,
  saveForm: PropTypes.func.isRequired,
  currentExtensionsTermsSum: PropTypes.number.isRequired,
  ...WithLoadingPropTypes,
};

export default memo(AddExtensionForm);
